import React, { useEffect, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';

const MAX_DOTS = 480;
const LEVELS = 12;
const FRAME_INTERVAL = 1000 / 15;
const MAX_PIXELS = 250_000; // At most 1 MB of bitmap memory per mounted plot.

// One small canvas, with no React updates during animation. Text/table
// skeletons stay separate so this effect only occupies the plot area.
export default function DotWaveLoader({ height = 260, sx, label = 'Loading chart', decorative = false }) {
    const canvasRef = useRef(null);
    const [canvasReady, setCanvasReady] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || typeof window.CanvasRenderingContext2D === 'undefined') return undefined;
        const context = canvas.getContext('2d');
        if (!context) return undefined;
        setCanvasReady(true);
        const surface = canvas.parentElement;
        const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const buckets = Array.from({ length: LEVELS }, () => []);
        let dots = [];
        let width = 0;
        let plotHeight = 0;
        let spacing = 14;
        let frame = 0;
        let idle = 0;
        let frameInterval = FRAME_INTERVAL;
        let color = '';
        let phase = 0;
        let lastFrame = -Infinity;
        let inView = true;
        let disposed = false;

        const paint = () => {
            const started = performance.now();
            context.clearRect(0, 0, width, plotHeight);
            context.fillStyle = color;
            buckets.forEach((bucket) => { bucket.length = 0; });
            const centerX = 0.5 + 0.24 * Math.sin(phase * 0.48);
            const centerY = 0.5 + 0.22 * Math.cos(phase * 0.37);
            for (const dot of dots) {
                const distance = (dot.nx - centerX) ** 2 * 7 + (dot.ny - centerY) ** 2 * 9;
                const swell = Math.exp(-distance);
                const wave = (Math.sin(dot.phase - phase * 1.2) + Math.cos(dot.crossPhase + phase * 0.8) + 2) / 4;
                const strength = Math.min(1, (0.68 * swell + 0.32 * wave) * dot.edge);
                buckets[Math.floor(strength * (LEVELS - 1))].push(dot);
            }
            buckets.forEach((bucket, index) => {
                const strength = index / (LEVELS - 1);
                const radius = spacing * (0.035 + strength * 0.17);
                context.globalAlpha = 0.10 + strength * 0.52;
                context.beginPath();
                bucket.forEach(({ x, y }) => {
                    context.moveTo(x + radius, y);
                    context.arc(x, y, radius, 0, Math.PI * 2);
                });
                context.fill();
            });
            context.globalAlpha = 1;
            // Slow devices get a static-looking, much slower wave rather than
            // spending the application's frame budget on decorative work.
            if (performance.now() - started > 3) frameInterval = 200;
        };
        const visible = () => !disposed && inView && document.visibilityState !== 'hidden' && width > 0 && plotHeight > 0;
        const queuePaint = () => {
            if (idle) return;
            if (window.requestIdleCallback) {
                // No timeout: data loading and rendering always get priority.
                idle = window.requestIdleCallback((deadline) => {
                    idle = 0;
                    if (visible() && deadline.timeRemaining() >= 3) paint();
                });
            } else paint();
        };
        const animate = (now) => {
            frame = 0;
            if (!visible() || motion?.matches) return;
            if (now - lastFrame >= frameInterval) {
                if (Number.isFinite(lastFrame)) phase += Math.min(0.1, (now - lastFrame) / 1000);
                lastFrame = now;
                queuePaint();
            }
            frame = window.requestAnimationFrame(animate);
        };
        const resume = () => {
            if (frame) window.cancelAnimationFrame(frame);
            if (idle) window.cancelIdleCallback?.(idle);
            frame = 0;
            idle = 0;
            lastFrame = -Infinity;
            if (!visible()) return;
            paint();
            if (!motion?.matches) frame = window.requestAnimationFrame(animate);
        };
        const resize = () => {
            const bounds = surface.getBoundingClientRect();
            width = bounds.width;
            plotHeight = bounds.height;
            color = window.getComputedStyle(canvas).color;
            if (width > 0 && plotHeight > 0) {
                const ratio = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(MAX_PIXELS / (width * plotHeight)));
                canvas.width = Math.max(1, Math.floor(width * ratio));
                canvas.height = Math.max(1, Math.floor(plotHeight * ratio));
                context.setTransform(ratio, 0, 0, ratio, 0, 0);
                spacing = Math.max(14, Math.sqrt(width * plotHeight / MAX_DOTS));
                const columns = Math.max(1, Math.floor(width / spacing));
                const rows = Math.max(1, Math.floor(plotHeight / spacing));
                dots = Array.from({ length: Math.min(MAX_DOTS, columns * rows) }, (_, index) => {
                    const x = (width - columns * spacing) / 2 + (index % columns + 0.5) * spacing;
                    const y = (plotHeight - rows * spacing) / 2 + (Math.floor(index / columns) + 0.5) * spacing;
                    const nx = x / width; const ny = y / plotHeight;
                    return { x, y, nx, ny, phase: nx * 6.3 + ny * 2.2, crossPhase: ny * 5.4 - nx * 3.8, edge: 0.2 + 0.8 * Math.sqrt(Math.max(0, Math.sin(Math.PI * nx) * Math.sin(Math.PI * ny))) };
                });
            }
            resume();
        };
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
        const intersectionObserver = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; resume(); });
        resizeObserver?.observe(surface);
        intersectionObserver?.observe(surface);
        if (!resizeObserver) window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', resume);
        motion?.addEventListener?.('change', resume);
        resize();
        return () => {
            disposed = true;
            if (frame) window.cancelAnimationFrame(frame);
            if (idle) window.cancelIdleCallback?.(idle);
            resizeObserver?.disconnect();
            intersectionObserver?.disconnect();
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', resume);
            motion?.removeEventListener?.('change', resume);
            canvas.width = 0;
            canvas.height = 0;
        };
    }, [theme]);

    return <Box role={decorative ? undefined : 'status'} aria-label={decorative ? undefined : label} aria-busy={decorative ? undefined : true} aria-hidden={decorative ? true : undefined}
        sx={{ position: 'relative', width: '100%', height, minHeight: 0, overflow: 'hidden', borderRadius: 1, color: 'text.primary', bgcolor: 'transparent', ...sx }}>
        <Box aria-hidden="true" sx={{ display: canvasReady ? 'none' : 'block', position: 'absolute', inset: 0, opacity: 0.18, backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1.3px)', backgroundSize: '16px 16px' }} />
        <canvas ref={canvasRef} aria-hidden="true" style={{ display: 'block', width: '100%', height: '100%', color: 'inherit' }} />
    </Box>;
}
